import AddBoxIcon from "@mui/icons-material/AddBox";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import { keyframes, styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { skipToken } from "@reduxjs/toolkit/query";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";
import Guid from "_shared/guids/guid";
import { uniq } from "lodash";
import {
  createMRTColumnHelper,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_Row,
  type MRT_RowSelectionState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { MaskedTeam } from "work/_shared/masked-fields";
import FeeScheduleLink from "work/fee-schedule-templates/_views/links/fee-schedule-template-link";
import ProposalRequestDraftDialog from "work/proposal-requests/view/proposal-request-draft-dialog";
import {
  HumanReadableProposalStatus,
  ProposalBoxType,
  ProposalOrderField,
  ProposalStatus,
} from "work/proposals/__constants/constants";
import {
  useBulkDeleteProposalsMutation,
  useGetProposalsQuery
} from "work/proposals/_api/proposal-api";
import ProposalQuery, {
  ProposalFilterParameters,
  ProposalOrderParameters,
} from "work/proposals/_api/queries/proposal-query";
import {
  addProposal,
  getLastSuccessfulProposalQueryByInterface,
  getProposalQueryByInterface,
  getProposalsByQuery,
  isProposalLoadIssue,
  ProposalLoadIssue,
  updateProposalInterfaceQuery
} from "work/proposals/_store/proposals-redux-slice";
import CreatorLink from "work/proposals/_views/_links/creator-link/creator-link";
import ProposalToolbar from "work/proposals/_views/_toolbars/proposal-toolbar";
import { PageTab } from "work/proposals/_views/proposals-page-tabs";
import Proposal from "work/proposals/proposal";
import VendorTeamLink from "work/work-agreements/teams/vendor-team/_views/vendor-team-link";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const RowTitle = styled(Typography)(() => ({
  fontSize: "1.1em",
  fontWeight: "600",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const columnHelper = createMRTColumnHelper<Proposal | ProposalLoadIssue>();

export default function ProposalsPage() {
  const isDialogOpenRef = useRef(false);

  const { openDialog, closeDialog, numDialogs } = useDialog();
  const { pageTab: pageTabParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const location = useLocation();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  
  const pageTab = pageTabParam as PageTab | undefined;
  const effectivePageTab = pageTab ?? PageTab.Active;
  const interfaceName = `proposalsPage:${effectivePageTab}`;

  const proposalsQueryParams = useSelector(getProposalQueryByInterface(interfaceName));

  const paginatedProposalsJson = useSelector(getProposalsByQuery(proposalsQueryParams));
  const paginatedProposals = useMemo(() => {
    if (!paginatedProposalsJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const proposalJson of paginatedProposalsJson.data) {
      try {
        if (isProposalLoadIssue(proposalJson)) {
          deserializedData.push(proposalJson);
        } else {
          const proposal = Proposal.fromJSON({
            json: proposalJson,
            currentUser: session.user!,
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", proposalJson);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in proposalJson ? (Guid.fromJSON(proposalJson.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [paginatedProposalsJson, session.user]);

  const proposalsAPIQuery = useGetProposalsQuery(
    proposalsQueryParams ? { query: proposalsQueryParams, interfaceName } : skipToken,
  );

  // TODO: make sure to namespace by tab to avoid showing incorrect counts/data when switching tabs
  const lastSuccessfulQueryResponseJson = useSelector(getLastSuccessfulProposalQueryByInterface(interfaceName));

  const lastSuccessfulQueryResponse = useMemo(() => {
    if (!lastSuccessfulQueryResponseJson) return;
    const deserializedData: ((Proposal | ProposalLoadIssue) & { id: Guid })[] = [];
    for (const item of lastSuccessfulQueryResponseJson.data) {
      try {
        if (isProposalLoadIssue(item)) {
          deserializedData.push(item);
        } else {
          const proposal = Proposal.fromJSON({
            json: item,
            currentUser: session.user!,
            options: { withRedline: false },
          });
          if (proposal?.id) {
            deserializedData.push(proposal as Proposal & { id: Guid });
          } else {
            console.error("Proposal does not have an id", item);
            const issue = { id: Guid.EMPTY, issue: "Error loading Proposal." };
            deserializedData.push(issue);
          }
        }
      } catch (error) {
        const id = "id" in item ? (Guid.fromJSON(item.id) ?? Guid.EMPTY) : Guid.EMPTY;
        deserializedData.push({ id, issue: "Error loading Proposal." });
        console.error("Error deserializing Proposal JSON:", error);
      }
    }
    return deserializedData;
  }, [lastSuccessfulQueryResponseJson, session.user]);

  const tableProposals = paginatedProposals ?? lastSuccessfulQueryResponse;

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: PaginationParameters.DEFAULT_PAGE_INDEX,
    pageSize: PaginationParameters.DEFAULT_PAGE_SIZE,
  });

  const [bulkDeleteProposalsQuery, bulkDeleteProposalsResult] = useBulkDeleteProposalsMutation();
  const [prevBulkDeleteFailureIdValues, setPrevBulkDeleteFailureIdValues] = useState<string[]>([]);
  const [bulkDeleteFailureIdValues, setBulkDeleteFailureIdValues] = useState<string[]>([]);

  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (!location.pathname.startsWith("/proposals/request") && !pageTab) {
      navigate(`/proposals/${PageTab.Active}`, { replace: true });
    }
  }, [location.pathname, navigate, pageTab]);

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    let boxType = searchParams.get("boxType") as ProposalBoxType | undefined;
    if (!boxType && (pageTab ?? PageTab.Active) === PageTab.Active) boxType = ProposalBoxType.AllActive;
    else if (!boxType && (pageTab ?? PageTab.Active) === PageTab.Drafts) boxType = ProposalBoxType.Draft;
    else if (!boxType && (pageTab ?? PageTab.Active) === PageTab.Archived) boxType = ProposalBoxType.Archive;

    const filterParameters = ProposalFilterParameters.fromSearchParams(searchParams, session, boxType);
    const orderParameters = ProposalOrderParameters.fromSearchParams(searchParams);

    const newQuery = new ProposalQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.fromSearchParams(searchParams),
    });
    if (proposalsQueryParams && newQuery.isEqualTo(proposalsQueryParams)) return;
    dispatch(
      updateProposalInterfaceQuery({
        interfaceName: interfaceName,
        query: newQuery,
      }),
    );
  }, [pageTab, session.user?.legalEntityId.value, session, searchParams, proposalsQueryParams, dispatch, interfaceName]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (globalFilter) {
      newSearchParams.set("query", globalFilter);
    }

    const statusFilter = columnFilters.find((filter) => filter.id === "status")?.value as string | undefined;
    if (statusFilter) {
      newSearchParams.set("status", statusFilter);
    }

    const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value as string | undefined;
    if (nameFilter) {
      newSearchParams.set("name", nameFilter);
    }

    const descriptionFilter = columnFilters.find((filter) => filter.id === "description")?.value as string | undefined;
    if (descriptionFilter) {
      newSearchParams.set("description", descriptionFilter);
    }

    const creatorFilter = columnFilters.find((filter) => filter.id === "creator")?.value as string | undefined;
    if (creatorFilter) {
      newSearchParams.set("creator", creatorFilter);
    }

    const negotiableFilter = columnFilters.find((filter) => filter.id === "negotiable")?.value as string | undefined;
    if (negotiableFilter !== undefined) {
      newSearchParams.set("negotiable", negotiableFilter);
    }

    const orderingField = sorting.find((sort) =>
      Object.values(ProposalOrderField)
        .map((field) => field.toLowerCase())
        .includes(sort.id.toLowerCase()),
    );

    if (orderingField) {
      newSearchParams.set("orderBy", orderingField.id);
      newSearchParams.set("orderDir", orderingField.desc ? OrderDirection.Descending : OrderDirection.Ascending);
    }

    const paginationPageIndex = pagination.pageIndex;
    if (paginationPageIndex !== undefined) newSearchParams.set("page", (paginationPageIndex + 1).toString());

    const paginationPageSize = pagination.pageSize;
    if (paginationPageSize !== undefined) newSearchParams.set("pageSize", paginationPageSize.toString());

    const boxType = searchParams.get("boxType") as ProposalBoxType | undefined;
    if(boxType) newSearchParams.set("boxType", boxType.toString());
    
    if (newSearchParams.toString() === searchParams.toString()) return;

    setSearchParams(newSearchParams);
  }, [globalFilter, columnFilters, sorting, pagination, searchParams, setSearchParams]);

  useEffect(() => {
    if (proposalsAPIQuery.isError) {
      enqueueSnackbar("Error loading proposal table", { variant: "error" });
    }
  }, [proposalsAPIQuery.isError]);

  useEffect(() => {
    if (!location.pathname.startsWith("/proposals/request")) return;
    if (isDialogOpenRef.current || numDialogs > 0) return;

    isDialogOpenRef.current = true;

    openDialog({
      title: "Request Proposal",
      resetStack: true,
      onClose: () => {
        isDialogOpenRef.current = false;
        navigate("/proposals/drafts", { replace: true });
      },
      component: (
        <ProposalRequestDraftDialog
          onCancel={() => {
            isDialogOpenRef.current = false;
            navigate("/proposals/drafts", { replace: true });
            closeDialog();
          }}
          onProposalSubmitted={(submittedProposal: Proposal) => {
            navigate(`/proposals/active/revise/${submittedProposal.id}`, {
              replace: true
            });
            dispatch(addProposal(submittedProposal));
            closeDialog();
          }}
        />
      ),
    });
  }, [closeDialog, dispatch, location.pathname, navigate, numDialogs, openDialog, pageTab]);

  const handleBulkDraftProposalDelete = useCallback(async () => {
    setPrevBulkDeleteFailureIdValues([...bulkDeleteFailureIdValues]);
    const selectedRowIdValues = Object.keys(rowSelection).filter((id) => rowSelection[id]);

    const response = await confirm({
      title: "Delete Proposals?",
      message: `Are you sure you want to delete ${selectedRowIdValues.length} proposal(s)?`,
      okButtonText: "Delete",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const failedDeletionsResponse: Array<string> =
        (await bulkDeleteProposalsQuery({
          ids: selectedRowIdValues,
        }).unwrap()) ?? [];
      if (failedDeletionsResponse.length > 0) {
        setBulkDeleteFailureIdValues(uniq(bulkDeleteFailureIdValues.concat(failedDeletionsResponse)));
      }

      if (failedDeletionsResponse.length === selectedRowIdValues.length) {
        const error = bulkDeleteProposalsResult.error;
        console.error(error);
        enqueueSnackbar("Failed to delete proposals", { variant: "error" });
        setRowSelection({});
        return;
      }

      if (failedDeletionsResponse.length > 0) {
        enqueueSnackbar(`${failedDeletionsResponse.length} proposals could not be deleted`, {
          variant: "warning",
        });
      } else {
        enqueueSnackbar(`${selectedRowIdValues.length} Proposals deleted successfully`, { variant: "success" });
      }
      setRowSelection({});
      if (proposalsQueryParams) {
        dispatch(
          updateProposalInterfaceQuery({
            interfaceName: interfaceName,
            query: proposalsQueryParams,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete proposals", { variant: "error" });
    }
  }, [
    bulkDeleteFailureIdValues, 
    bulkDeleteProposalsQuery, 
    bulkDeleteProposalsResult.error, 
    confirm, 
    dispatch, 
    interfaceName, 
    proposalsQueryParams, 
    rowSelection
  ]);

  const renderStatusColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue): ReactNode => {
      if (proposalsAPIQuery.isLoading) return;
      if (isProposalLoadIssue(proposal)) {
        return (
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: "4000px",
                  whiteSpace: "normal",
                },
              },
            }}
            title={`Our staff has been notified. Reference id: ${proposal.id.value}`}
          >
            <span>Error Loading Proposal</span>
          </Tooltip>
        );
      }

      const userId = session.user?.id;
      let proposalStatus: string = ProposalStatus[proposal.status]?.toString();
      let displayStatus: string = HumanReadableProposalStatus[proposalStatus as keyof typeof ProposalStatus];
      const isClient =
        MaskedTeam.isPresentNotMasked(proposal.clientLeader) && proposal.clientLeader?.userId.isEqualTo(userId);
      const isTeamLeader =
        MaskedTeam.isPresentNotMasked(proposal.vendorTeam) && proposal.vendorTeam?.leader?.userId.isEqualTo(userId);
      const isVendor = proposal.additionalVendors?.some((vendor) => vendor.userId.isEqualTo(userId));

      if (
        (proposal.status === ProposalStatus.AwaitingApprovalByClient && isClient) ||
        (proposal.status === ProposalStatus.AwaitingApprovalByTeamLeader && isTeamLeader) ||
        (proposal.status === ProposalStatus.AwaitingApprovalByVendors && isVendor)
      )
        displayStatus = "Awaiting Your Approval";

      // If the proposal is awaiting review by the user
      if (
        proposal.clientReviewers?.some(
          (reviewer) => session.user?.id?.isEqualTo(reviewer.userId) && !reviewer.dateApproved,
        ) ||
        proposal.vendorReviewers?.some(
          (reviewer) => session.user?.id?.isEqualTo(reviewer.userId) && !reviewer.dateApproved,
        )
      ) {
        displayStatus = "Awaiting Your Review";
      }
      if (proposal.id?.value && bulkDeleteFailureIdValues.includes(proposal.id.value)) {
        return (
          <>
            <span>{displayStatus}</span>
            <br />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "0.7rem",
                color: "error.main",
              }}
            >
              Cannot delete this proposal
            </Box>
          </>
        );
      }

      return displayStatus;
    },
    [bulkDeleteFailureIdValues, proposalsAPIQuery.isLoading, session.user?.id],
  );

  const renderCreatorColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (proposalsAPIQuery.isLoading) return;
      if ((isProposalLoadIssue(proposal) || !proposal.creator)) return <Icon>remove</Icon>;
      if (proposal.creator.userId.isEqualTo(session.user?.id)) {
        return "You";
      }
      return <CreatorLink creator={proposal.creator} />;
    },
    [session.user?.id, proposalsAPIQuery.isLoading],
  );

  const renderTeamColumn = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (proposalsAPIQuery.isLoading) return;
      if (isProposalLoadIssue(proposal) || !proposal.vendorTeam) return <Icon>remove</Icon>;
      return <VendorTeamLink proposal={proposal} session={session} />;
    },
    [session, proposalsAPIQuery.isLoading],
  );

  const renderFeeScheduleColumn = useCallback((proposal: Proposal | ProposalLoadIssue) => {
    if (proposalsAPIQuery.isLoading) return;
    if (isProposalLoadIssue(proposal) || !proposal.feeSchedule) return <Icon>remove</Icon>;
    return <FeeScheduleLink proposal={proposal} />;
  }, [proposalsAPIQuery.isLoading]);

  const statusFilterOptions = useMemo(
    () =>
      Object.entries(HumanReadableProposalStatus).map(([key, value]) => ({
        value: key,
        label: value,
      })),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        header: "Status",
        id: "status",
        filterVariant: "select",
        filterSelectOptions: statusFilterOptions,
        Cell: ({ row }) => renderStatusColumn(row.original),
      }),
      columnHelper.display({
        header: "Name",
        id: "name",
        Cell: NameCell,
      }),
      columnHelper.display({
        header: "Description",
        id: "description",
        Cell: DescriptionCell,
      }),
      columnHelper.display({
        header: "Creator",
        id: "creator",
        Cell: ({ row }) => renderCreatorColumn(row.original),
      }),
      columnHelper.display({
        header: "Negotiable",
        id: "negotiable",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: [
          { value: "true", label: "Negotiable" },
          { value: "false", label: "Non-negotiable" },
        ],
        Cell: NegotiableCell,
      }),
      columnHelper.display({
        header: "Team",
        id: "team",
        size: 50,
        Cell: ({ row }) => renderTeamColumn(row.original),
      }),
      columnHelper.display({
        header: "Fee Schedule",
        id: "feeSchedule",
        size: 50,
        Cell: ({ row }) => renderFeeScheduleColumn(row.original),
      }),
      columnHelper.display({
        header: "Response Due By",
        id: "responseDueBy",
        size: 50,
        Cell: ResponseDueByCell,
      }),
      columnHelper.display({
        header: "Start Date",
        id: "startDate",
        size: 50,
        Cell: StartDateCell,
      }),
      columnHelper.display({
        header: "End Date",
        id: "endDate",
        size: 50,
        Cell: EndDateCell,
      }),
      columnHelper.display({
        header: "Hire Date",
        id: "hireDate",
        size: 50,
        Cell: HireDateCell,
      }),
      columnHelper.display({
        header: "Archive Date",
        id: "archiveDate",
        size: 50,
        Cell: ArchiveDateCell,
      }),
    ],
    [statusFilterOptions, renderStatusColumn, renderCreatorColumn, renderTeamColumn, renderFeeScheduleColumn],
  );

  const openProposal = useCallback(
    (proposal: Proposal | ProposalLoadIssue) => {
      if (isProposalLoadIssue(proposal)) {
        console.warn("Cannot open proposal with loading issues.");
        return;
      }
      if (!proposal.id) {
        console.error("Proposal ID is required");
        return;
      }
      const editable = proposal.availableActions.Edit.some((userId) => userId.isEqualTo(session.user?.id));
      const revisable = proposal.availableActions.Revise.some((userId) => userId.isEqualTo(session.user?.id));
      const reviewable = proposal.availableActions.Review.some((userId) => userId.isEqualTo(session.user?.id));
      const submittable = proposal.availableActions.Submit.some((userId) => userId.isEqualTo(session.user?.id));

      let tab: PageTab = pageTab ?? PageTab.Active;
      let action: "view" | "edit" | "revise" | "review" = "view";

      if (editable && !revisable) {
        tab = PageTab.Drafts;
        action = "edit";
      }
      if (revisable) {
        if (submittable) {
          tab = PageTab.Drafts;
        } else {
          tab = PageTab.Active;
        }
        action = "revise";
      }
      if (reviewable) {
        action = "review";
      }

      navigate(`/proposals/${tab}/${action}/${proposal.id}`);
    },
    [pageTab, navigate, session.user?.id],
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => {
      return <ProposalToolbar table={table} activeTab={pageTab ?? PageTab.Active} />;
    },
    [pageTab],
  );

  const renderToolbarInternalActions = useCallback(
    (props: { table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={props.table} />
              <ToggleFiltersButton table={props.table} />
              <ShowHideColumnsButton table={props.table} />
              <ToggleDensePaddingButton table={props.table} />
              <ToggleFullScreenButton table={props.table} />
            </ToolbarButtons>
            <Button
              startIcon={<AddBoxIcon />}
              disabled={session.user?.isSuperUser}
              onClick={() => navigate("/proposals/drafts/create")}
            >
              Create Proposal
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton color="primary" onClick={() => navigate("/proposals/drafts/create")}>
            <AddBoxIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    [isMediumDisplaySize, session.user?.isSuperUser, navigate],
  );

  const failedOperationKeyFrame = keyframes`
    0% { background-color: rgba(255, 100, 100, 0.1); }
    50% { background-color: rgba(255, 100, 100, 0.3); }
    100% { background-color: rgba(255, 100, 100, 0.1); }
  `;
  const muiTableBodyRowProps = useCallback(
    ({ row }: { row: MRT_Row<Proposal | ProposalLoadIssue> }) => ({
      onClick: () => openProposal(row.original),
      sx: {
        backgroundColor: isProposalLoadIssue(row.original)
          ? "rgba(255, 100, 100, 0.1)" // light red for error
          : "inherit", // default color
        cursor: isProposalLoadIssue(row.original) ? "default" : "pointer",
        animation:
          row.original.id &&
            !prevBulkDeleteFailureIdValues.includes(row.original.id.value) &&
            bulkDeleteFailureIdValues.includes(row.original.id.value)
            ? `${failedOperationKeyFrame} 1s ease-in-out`
            : "none",
        animationIterationCount:
          row.original.id && bulkDeleteFailureIdValues.includes(row.original.id.value) ? "3" : "0",
      },
    }),
    [bulkDeleteFailureIdValues, failedOperationKeyFrame, openProposal, prevBulkDeleteFailureIdValues],
  );

  const muiToolbarAlertBannerProps = useMemo(() => {
    if (proposalsAPIQuery.isError) {
      return { color: "error" as const, children: "Error loading data" };
    }
    return {
      action: (
        <AlertActions>
          <Button color="primary" startIcon={<DeleteIcon />} onClick={handleBulkDraftProposalDelete}>
            Delete
          </Button>
        </AlertActions>
      ),
    };
  }, [proposalsAPIQuery.isError, handleBulkDraftProposalDelete]);

  const tableOptions = useMemo<MRT_TableOptions<Proposal | ProposalLoadIssue>>(() => {
    return {
      columns,
      data: tableProposals ?? [],
      getRowId: (row) => row.id?.value ?? Guid.generate().value,
      enableRowSelection:
        pageTab === PageTab.Drafts
          ? (row) => !!row.original.id && !bulkDeleteFailureIdValues.includes(row.original.id.value)
          : false,
      muiSelectCheckboxProps: ({ row }) => ({
        onClick: (event) => {
          event.stopPropagation();
          if (row.original.id && bulkDeleteFailureIdValues.includes(row.original.id.value)) {
            event.preventDefault();
          }
        },
        title:
          row.original.id && bulkDeleteFailureIdValues.includes(row.original.id.value)
            ? "Cannot delete this proposal"
            : undefined,
      }),
      enableRowActions: false,
      enableTableHead: true,
      muiTableBodyRowProps,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      enableColumnPinning: true,
      initialState: {
        columnPinning: { right: ["mrt-row-actions"] },
      },
      manualFiltering: true,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      muiTableContainerProps: {
        sx: {
          flex: 1,
          position: "relative"
        },
      },
      muiTablePaperProps: {
        sx: {
          height: "100%",
          display: "flex",
          flexDirection: "column"
        },
      },
      muiToolbarAlertBannerProps,
      rowCount: tableProposals?.length ?? 0,
      pageCount: Math.ceil((tableProposals?.length ?? 0) / pagination.pageSize),
      enablePagination: true,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setGlobalFilter,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
      state: {
        columnVisibility: {
          hireDate: pageTab === PageTab.Archived,
          archiveDate: pageTab === PageTab.Archived,
        },
        columnFilters,
        globalFilter,
        isLoading: proposalsAPIQuery.isFetching,
        pagination,
        showAlertBanner: Boolean(proposalsAPIQuery.isError),
        showProgressBars: proposalsAPIQuery.isFetching,
        sorting,
        rowSelection,
        showSkeletons: proposalsAPIQuery.isLoading,
      },
    };
  }, [
    columns,
    tableProposals,
    pageTab,
    muiTableBodyRowProps,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    muiToolbarAlertBannerProps,
    pagination,
    columnFilters,
    globalFilter,
    proposalsAPIQuery.isFetching,
    proposalsAPIQuery.isError,
    proposalsAPIQuery.isLoading,
    sorting,
    rowSelection,
    bulkDeleteFailureIdValues
  ]);

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToProposals) return <NotAuthorized />;

  return <MRTNoMemo<Proposal | ProposalLoadIssue> options={tableOptions} />;
}

const NameCell = ({ row, table }: { 
    row: { original: Proposal | ProposalLoadIssue },
    table: MRT_TableInstance<Proposal | ProposalLoadIssue>
  }) => {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.name?.value;
  return value ? <RowTitle>{value}</RowTitle> : <Icon>remove</Icon>;
};

const DescriptionCell = ({ row, table }: { 
  row: { original: Proposal | ProposalLoadIssue }, 
  table: MRT_TableInstance<Proposal | ProposalLoadIssue> 
}) => {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.description?.value;
  return value ? <Typography variant="body2">{value}</Typography> : <Icon>remove</Icon>;
};

const NegotiableCell = ({ row, table }: { 
    row: { original: Proposal | ProposalLoadIssue },
    table: MRT_TableInstance<Proposal | ProposalLoadIssue>
  }) => {
  if (table.getState().isLoading) return;
  let value: boolean | undefined;
  if (!isProposalLoadIssue(row.original)) {
    value = row.original.negotiable?.value;
  }
  if (value === undefined) {
    return <Icon>remove</Icon>;
  }
  return value ? <CheckIcon /> : <CloseIcon />;
};

function ResponseDueByCell({ row, table }: { 
  row: { original: Proposal | ProposalLoadIssue }, 
  table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.responseDueBy;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function StartDateCell({ row, table }: { 
  row: { original: Proposal | ProposalLoadIssue }, 
  table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.startDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function EndDateCell({ row, table }: { 
  row: { original: Proposal | ProposalLoadIssue }, 
  table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.endDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function HireDateCell({ row, table }: { 
  row: { original: Proposal | ProposalLoadIssue }, 
  table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.hireDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}

function ArchiveDateCell({ row, table }: { 
  row: { original: Proposal | ProposalLoadIssue }, 
  table: MRT_TableInstance<Proposal | ProposalLoadIssue> }) {
  if (table.getState().isLoading) return;
  const value = !isProposalLoadIssue(row.original) && row.original.archiveDate;
  return value ? value.format("MM/DD/YYYY") : <Icon>remove</Icon>;
}
